"use client";

import { Check, Loader2 } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useReferenceData } from "@/hooks/use-reference-data";
import { cn } from "@/lib/utils";
import type { ReferenceDataKey } from "@/types";

export type StatusTone =
  | "success"
  | "danger"
  | "warning"
  | "neutral"
  | "info";

// Default value → tone map. Covers the conventions we see across modules today.
// Anything not in here falls back to "neutral".
const DEFAULT_TONES: Record<string, StatusTone> = {
  active: "success",
  delivered: "success",
  approved: "success",
  completed: "success",
  blocked: "danger",
  canceled: "danger",
  cancelled: "danger",
  rejected: "danger",
  fired: "warning",
  pending: "warning",
  picked_up: "warning",
  in_progress: "warning",
  deleted: "neutral",
  inactive: "neutral",
  new: "info",
};

// One shared semantic palette for the Orders Status AND Payment columns, so the
// same colour means the same thing in both (the reader learns one system):
//   grey/neutral   → not started yet   (Status: new,       Payment: pending)
//   blue/info      → in progress       (Status: picked_up, Payment: invoiced, claim)
//   green/success  → complete / good   (Status: delivered, Payment: paid)
//   red/danger     → needs attention   (Payment: overdue)
//   grey/neutral   → exited workflow   (Status: deleted — soft-deleted, like everywhere)
// Applied automatically to any badge keyed on the order reference data (see
// REFERENCE_KEY_TONES), so every order status/payment badge in the app is
// consistent without a per-site toneMap — and no other module's tones change.
export const ORDER_TONES: Record<string, StatusTone> = {
  new: "neutral",
  picked_up: "info",
  delivered: "success",
  deleted: "neutral",
  pending: "neutral",
  invoiced: "info",
  claim: "info",
  paid: "success",
  overdue: "danger",
};

// Per-reference-data tone overrides, applied when a badge carries that
// `referenceKey` and no explicit `tone`/`toneMap` covers the value. Scopes a
// semantic palette to a resource without touching the global DEFAULT_TONES.
const REFERENCE_KEY_TONES: Partial<
  Record<ReferenceDataKey, Record<string, StatusTone>>
> = {
  order_statuses: ORDER_TONES,
  order_payment_statuses: ORDER_TONES,
};

const TONE_STYLES: Record<StatusTone, string> = {
  success: "bg-status-green/20 text-status-green",
  danger: "bg-status-red/20 text-status-red",
  warning: "bg-status-orange/20 text-status-orange",
  neutral: "bg-fg-muted/20 text-fg-muted",
  // `status-blue` (#c4e4e1) is a pale tint meant for backgrounds, not text — as
  // `text-status-blue` it was nearly invisible (e.g. the salary "Finalized"
  // badge). Use the vivid `tx-blue` base instead, the same readable
  // text-on-tint pattern the other tones and the audit-log badges use.
  info: "bg-tx-blue/15 text-tx-blue",
};

// Shared pill shape/typography (everything except colour). Every status pill
// uses this so they read identically across the app.
export const STATUS_BADGE_BASE =
  "inline-flex h-9 min-w-[120px] items-center justify-center rounded-full px-4 font-display text-xs font-semibold tracking-wider uppercase";

interface PillProps {
  value: string;
  referenceKey?: ReferenceDataKey;
  label?: string;
  tone?: StatusTone;
  toneMap?: Record<string, StatusTone>;
  /** Raw colour classes (`bg-… text-…`) per value — escape hatch for palettes
   *  the StatusTone set can't express (e.g. audit-log events use purple, and
   *  `text-foreground` on pale tints). Wins over `tone`/`toneMap` when it has
   *  the value; otherwise falls back to the tone system. */
  colorMap?: Record<string, string>;
  className?: string;
}

// The presentational pill — resolves a label (from reference data or `label`)
// and a colour (colorMap → tone), then renders the shared shape. Private:
// callers use StatusBadge.
function Pill({
  value,
  referenceKey,
  label,
  tone,
  toneMap,
  colorMap,
  className,
}: PillProps) {
  const query = useReferenceData();
  const labelFromRef = referenceKey
    ? query.data?.[referenceKey]?.find((item) => item.value === value)?.label
    : undefined;
  const resolvedLabel = label ?? labelFromRef ?? value;
  const resolvedTone =
    tone ??
    toneMap?.[value] ??
    (referenceKey ? REFERENCE_KEY_TONES[referenceKey]?.[value] : undefined) ??
    DEFAULT_TONES[value] ??
    "neutral";
  const colorClass = colorMap?.[value] ?? TONE_STYLES[resolvedTone];

  return (
    <span className={cn(STATUS_BADGE_BASE, colorClass, className)}>
      {resolvedLabel}
    </span>
  );
}

// The one status badge, used everywhere. Renders a pill and — when interactive
// (`onSelect` + `referenceKey`, with options + permission) — opens a menu of
// selectable statuses from reference data. Omit `onSelect` (or pass `disabled`)
// for a read-only badge; for non-reference-data badges pass `label` + `colorMap`
// directly (e.g. audit-log events). Colours: `tone`/`toneMap` (StatusTone), or
// `colorMap` for raw classes the tones can't express.
export function StatusBadge({
  value,
  referenceKey,
  label,
  tone,
  toneMap,
  colorMap,
  onSelect,
  disabled = false,
  pending = false,
  excludeValues = [],
  align = "end",
  className,
}: PillProps & {
  /** Omit to render a static badge (menu disabled). */
  onSelect?: (value: string) => void;
  disabled?: boolean;
  pending?: boolean;
  /** Status values to omit from the menu (e.g. "deleted"). */
  excludeValues?: string[];
  align?: "start" | "center" | "end";
}) {
  // Full query (not the keyed overload) so this stays valid when referenceKey is
  // omitted (read-only badge); options are empty without a key.
  const refData = useReferenceData();
  const options = (
    referenceKey ? (refData.data?.[referenceKey] ?? []) : []
  ).filter((o) => !excludeValues.includes(o.value));

  const pillProps: PillProps = {
    value,
    referenceKey,
    label,
    tone,
    toneMap,
    colorMap,
  };

  // Static when there's nothing to pick, no handler, no key, or no permission.
  if (disabled || !onSelect || !referenceKey || options.length === 0) {
    return <Pill {...pillProps} className={className} />;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={pending}
        className={cn(
          "group relative inline-flex rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
          className,
        )}
      >
        {/* Badge keeps its size; the spinner overlays it so the row doesn't shift. */}
        <span className={cn("transition-opacity", pending && "opacity-40")}>
          <Pill {...pillProps} />
        </span>
        {pending && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="size-4 animate-spin text-fg-muted" />
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="min-w-[180px]">
        {options.map((opt) => {
          const isCurrent = opt.value === value;
          return (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => {
                if (!isCurrent) onSelect(opt.value);
              }}
              className="justify-between gap-3 font-display"
            >
              <span className="flex items-center gap-2">
                <Pill
                  value={opt.value}
                  label={opt.label}
                  referenceKey={referenceKey}
                  toneMap={toneMap}
                  colorMap={colorMap}
                  className="h-6 min-w-0 px-2.5 text-[10px]"
                />
              </span>
              {isCurrent && <Check className="size-4 text-foreground" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
